import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceDNABibleApi,
  type AIContextRequirement,
  type ContextValidationResult,
  type DNABibleDashboard,
  type DNABibleStatus,
  type DNALayerSpec,
  type EntityModelEntry,
  type KnowledgeGraphEdge,
  type KnowledgeGraphEntity,
  type LearningTrigger,
  type MarketplaceDNAProfile,
  type PhilosophyPillar,
  type RelationshipType,
} from '../../api/marketplace-dna-bible';

type Tab = 'overview' | 'dna' | 'profiles' | 'graph' | 'ai-context' | 'learning' | 'status';
type Theme = 'light' | 'dark';

const MANDATORY_CONTEXT: Record<string, boolean> = {
  marketplace_dna: true, knowledge_graph: true, historical_experiments: true,
  business_kpis: true, semantic_context: true, profit_context: true,
  inventory_context: true, advertising_context: true, listing_context: true,
};

export function MarketplaceDNABiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<DNABibleDashboard | null>(null);
  const [philosophy, setPhilosophy] = useState<PhilosophyPillar[]>([]);
  const [layers, setLayers] = useState<DNALayerSpec[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<DNALayerSpec | null>(null);
  const [profiles, setProfiles] = useState<MarketplaceDNAProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<MarketplaceDNAProfile | null>(null);
  const [entities, setEntities] = useState<KnowledgeGraphEntity[]>([]);
  const [edges, setEdges] = useState<KnowledgeGraphEdge[]>([]);
  const [relTypes, setRelTypes] = useState<RelationshipType[]>([]);
  const [aiContext, setAiContext] = useState<AIContextRequirement[]>([]);
  const [triggers, setTriggers] = useState<LearningTrigger[]>([]);
  const [entityModel, setEntityModel] = useState<EntityModelEntry[]>([]);
  const [status, setStatus] = useState<DNABibleStatus | null>(null);
  const [contextResult, setContextResult] = useState<ContextValidationResult | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, phil, lyrs, profs, ents, edgs, rels, ctx, trg, model, st] = await Promise.all([
        marketplaceDNABibleApi.dashboard(),
        marketplaceDNABibleApi.philosophy(),
        marketplaceDNABibleApi.layers(),
        marketplaceDNABibleApi.profiles(),
        marketplaceDNABibleApi.graphEntities(),
        marketplaceDNABibleApi.graphEdges(),
        marketplaceDNABibleApi.relationshipTypes(),
        marketplaceDNABibleApi.aiContext(),
        marketplaceDNABibleApi.learningTriggers(),
        marketplaceDNABibleApi.entityModel(),
        marketplaceDNABibleApi.status(),
      ]);
      setDashboard(dash);
      setPhilosophy(phil);
      setLayers(lyrs);
      setProfiles(profs);
      setEntities(ents);
      setEdges(edgs);
      setRelTypes(rels);
      setAiContext(ctx);
      setTriggers(trg);
      setEntityModel(model);
      setStatus(st);
      if (profs.length > 0 && !selectedProfile) setSelectedProfile(profs[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load DNA bible');
    }
  }, [selectedProfile]);

  useEffect(() => { refresh(); }, [refresh]);

  const loadLayer = async (key: string) => {
    try {
      const layer = await marketplaceDNABibleApi.layer(key);
      setSelectedLayer(layer);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load layer');
    }
  };

  const validateContext = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceDNABibleApi.validateContext({
        asin: selectedProfile?.asin ?? 'B0DEMO001',
        provided: MANDATORY_CONTEXT,
      });
      setContextResult(result);
      setActionMsg(result.complete ? 'AI context complete — recommendation may proceed' : `Missing: ${result.missing?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'dna', label: 'DNA Layers' },
    { id: 'profiles', label: 'ASIN Profiles' },
    { id: 'graph', label: 'Knowledge Graph' },
    { id: 'ai-context', label: 'AI Context' },
    { id: 'learning', label: 'Continuous Learning' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">MIB-000003</p>
          <h1 className="text-2xl font-semibold">Marketplace DNA & Knowledge Graph Bible</h1>
          <p className={`text-sm ${muted}`}>
            Product DNA engine, knowledge graph, AI context framework and continuous learning architecture
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-bible" className="text-violet-500 hover:underline">MIB-000001</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-kpi-bible" className="text-violet-500 hover:underline">MIB-000002</Link>
            <span className={muted}>·</span>
            <Link to="/amazon/market-intel" className="text-violet-500 hover:underline">Market Intelligence</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-violet-600 text-white' : `${card} ${muted}`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Bible ID', value: dashboard.bible_id },
              { label: 'DNA Layers', value: dashboard.dna_layers },
              { label: 'Graph Entities', value: dashboard.graph_entities },
              { label: 'Relationship Types', value: dashboard.relationship_types },
              { label: 'AI Context Reqs', value: dashboard.ai_context_requirements },
              { label: 'Learning Triggers', value: dashboard.learning_triggers },
              { label: 'Sample Profiles', value: dashboard.sample_profiles },
              { label: 'Status', value: 'Intelligence Core Active' },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Product Philosophy</h3>
            <p className={`mt-1 text-sm ${muted}`}>Every ASIN has continuously evolving DNA — not isolated metrics.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {philosophy.map((p) => (
                <span key={p.key} className="rounded-full bg-violet-100 px-3 py-1 text-xs dark:bg-violet-900">{p.name}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'dna' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="grid gap-2 md:grid-cols-2">
            {layers.map((l) => (
              <button key={l.key} type="button" onClick={() => loadLayer(l.key)} className={`rounded-xl border p-3 text-left ${card} hover:border-violet-400`}>
                <p className="font-medium">{l.name}</p>
                <p className={`text-xs ${muted}`}>{l.fields.length} fields · {l.required ? 'required' : 'optional'}</p>
              </button>
            ))}
          </div>
          {selectedLayer && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="text-lg font-semibold">{selectedLayer.name}</h3>
              <p className={`mt-1 text-sm ${muted}`}>{selectedLayer.description}</p>
              <ul className={`mt-3 space-y-1 text-sm ${muted}`}>
                {selectedLayer.fields.map((f) => (
                  <li key={f.key}><span className="font-mono text-xs text-violet-500">{f.key}</span> — {f.name} ({f.data_type})</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'profiles' && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            {profiles.map((p) => (
              <button key={p.asin} type="button" onClick={() => setSelectedProfile(p)} className={`rounded-xl border p-4 text-left ${card} ${selectedProfile?.asin === p.asin ? 'border-violet-500' : ''}`}>
                <div className="flex justify-between">
                  <span className="font-mono text-sm">{p.asin}</span>
                  <span className="text-lg font-semibold text-violet-500">{p.overall_score.toFixed(1)}</span>
                </div>
                <p className={`text-sm ${muted}`}>{p.brand} · {p.category} · {p.lifecycle_stage}</p>
              </button>
            ))}
          </div>
          {selectedProfile && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">Layer Scores — {selectedProfile.asin}</h3>
              <div className="mt-3 grid gap-2 md:grid-cols-3">
                {Object.entries(selectedProfile.layers).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                    <span className="font-medium">{v.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Validate AI Context (§16)</h3>
            <p className={`mt-1 text-sm ${muted}`}>No recommendation shall be generated without complete product context.</p>
            <button type="button" disabled={busy} onClick={validateContext} className="mt-3 rounded-lg bg-violet-600 px-4 py-2 text-sm text-white disabled:opacity-50">
              {busy ? 'Validating…' : 'Validate Context for Recommendation'}
            </button>
            {contextResult && (
              <p className={`mt-2 text-sm ${contextResult.complete ? 'text-emerald-500' : 'text-amber-500'}`}>
                {contextResult.complete ? 'Context complete' : `Missing: ${contextResult.missing?.join(', ')}`}
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'graph' && (
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Graph Entities</h2>
            <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4">
              {entities.map((e) => (
                <div key={e.key} className={`rounded-lg border p-3 text-sm ${card}`}>
                  <span className="font-medium">{e.name}</span>
                  <p className={`text-xs ${muted}`}>{e.entity_type}</p>
                  {e.connections && <p className={`mt-1 text-xs ${muted}`}>→ {e.connections.slice(0, 3).join(', ')}</p>}
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h2 className="mb-3 text-lg font-semibold">Relationship Types</h2>
              <div className="space-y-1">
                {relTypes.map((r) => (
                  <div key={r.key} className={`rounded-lg border px-4 py-2 text-sm ${card}`}>
                    <span className="font-medium">{r.name}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{r.directional ? 'directed' : 'undirected'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="mb-3 text-lg font-semibold">Sample Edges</h2>
              <div className="space-y-1">
                {edges.map((e, i) => (
                  <div key={i} className={`rounded-lg border px-4 py-2 font-mono text-xs ${card}`}>
                    {e.from} —<span className="text-violet-500">[{e.relationship}]</span>→ {e.to}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Entity Model</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {entityModel.map((e) => (
                <div key={e.entity} className={`rounded-lg border p-3 ${card}`}>
                  <span className="font-medium">{e.entity}</span>
                  <p className={`text-xs ${muted}`}>{e.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'ai-context' && (
        <div className="grid gap-3 md:grid-cols-2">
          {aiContext.map((r) => (
            <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{r.name}</h3>
                {r.mandatory && <span className="text-xs text-violet-500">Mandatory</span>}
              </div>
              <p className={`mt-1 text-sm ${muted}`}>Source: {r.source}</p>
              {r.description && <p className={`mt-1 text-xs ${muted}`}>{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'learning' && (
        <div className="grid gap-3 md:grid-cols-2">
          {triggers.map((t) => (
            <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">{t.name}</h3>
              <p className={`mt-1 text-sm ${muted}`}>{t.description}</p>
              {t.updates_layers && t.updates_layers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {t.updates_layers.map((l) => (
                    <span key={l} className="rounded bg-violet-100 px-2 py-0.5 font-mono text-xs dark:bg-violet-900">{l}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="grid gap-4 md:grid-cols-2">
            {Object.entries(status).map(([key, value]) => (
              <div key={key}>
                <p className={`text-xs uppercase ${muted}`}>{key.replace(/_/g, ' ')}</p>
                <p className="mt-1 font-medium">{Array.isArray(value) ? (value as string[]).join(', ') : String(value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
